import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, money, priceView } from "@/lib/api";
import { ConditionBadge, MarketingBadge } from "@/components/store/Badges";
import { ShoppingCart, Zap, Timer, Share2, Heart, Facebook, MessageCircle, Copy, Twitter, Truck, ChevronRight, ShieldCheck, Image as ImageIcon, PackageX } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { HoldModal } from "@/components/store/HoldModal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [hold, setHold] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [holdCfg, setHoldCfg] = useState({ hold_enabled: true, hold_deposit_amount: 2, hold_min_price: 50 });
  const { addItem, toggleLocalWishlist, isInLocalWishlist } = useCart();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      document.title = `${r.data.product_name} · Dealswagon`;
      // JSON-LD structured data
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org", "@type": "Product",
        name: r.data.product_name, sku: r.data.sku, brand: r.data.brand,
        image: r.data.featured_image, description: r.data.description,
        offers: {
          "@type": "Offer", priceCurrency: "CAD", price: r.data.price,
          availability: (r.data.availability?.available ?? r.data.quantity) > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        },
      });
      script.id = "ld-product";
      document.getElementById("ld-product")?.remove();
      document.head.appendChild(script);
    }).catch(() => setNotFound(true));
    api.get("/settings").then((r) => setHoldCfg({
      hold_enabled: r.data.hold_enabled !== false,
      hold_deposit_amount: Number(r.data.hold_deposit_amount ?? 2),
      hold_min_price: Number(r.data.hold_min_price ?? 50),
    })).catch(() => {});
  }, [slug, navigate]);

  if (notFound) return (
    <main className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="product-unavailable">
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-brand-yellowLight text-brand-navy mb-5">
        <PackageX className="h-8 w-8" />
      </div>
      <h1 className="font-heading text-3xl text-brand-navy">This item is no longer available</h1>
      <p className="text-slate-600 mt-2">It may have sold out or been removed. Plenty more great deals are waiting for you.</p>
      <button onClick={() => navigate("/shop")} data-testid="product-unavailable-shop-btn"
        className="mt-6 inline-flex items-center gap-2 bg-brand-red hover:bg-brand-redHover text-white font-black uppercase tracking-wider px-8 h-12 rounded-full active:scale-95 transition">
        Browse the Shop
      </button>
    </main>
  );
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-16">Loading...</div>;

  const IMG_FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='600' height='600'%3E%3Crect width='100%25' height='100%25' fill='%23f1f5f9'/%3E%3C/svg%3E";

  const pv = priceView(product);
  const pct = pv.pct;
  const avail = product.availability || { available: product.quantity || 0, inventory_type: "STANDARD" };
  const outOfStock = avail.available <= 0;
  const unique = avail.inventory_type === "UNIQUE_ITEM";
  const maxQty = unique ? 1 : (avail.available || 1);
  const unavailableLabel = outOfStock && (avail.status === "HELD" || avail.status === "RESERVED") ? "Currently unavailable" : "Sold out";
  const wished = isInLocalWishlist(product.id);
  const images = product.images?.length ? product.images : [product.featured_image];

  const onAddToCart = async () => {
    const r = await addItem(product, qty);
    if (r.ok) toast.success("Added to cart");
    else toast.error(r.message);
  };
  const onBuyNow = async () => {
    const r = await addItem(product, qty);
    if (r.ok) navigate("/checkout");
    else toast.error(r.message);
  };
  const onHold = () => {
    if (!holdCfg.hold_enabled) {
      toast("Holds are not available right now");
      return;
    }
    if ((product.price || 0) < (holdCfg.hold_min_price || 0)) {
      toast("24-Hour Hold", { description: `Holds are available on items $${Math.round(holdCfg.hold_min_price)} and up.` });
      return;
    }
    setHold(true);
  };

  const shareUrl = window.location.href;
  const shareText = `Check out ${product.product_name} on Dealswagon`;
  const copyLink = async () => { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: product.product_name, text: shareText, url: shareUrl }); }
      catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };
  const shareTo = (name) => {
    const t = encodeURIComponent(shareText);
    const u = encodeURIComponent(shareUrl);
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      whatsapp: `https://api.whatsapp.com/send?text=${t}%20${u}`,
      twitter: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
    };
    window.open(urls[name], "_blank", "noopener");
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="product-detail-page">
      <nav className="text-xs text-slate-500 mb-6 flex items-center gap-1">
        <Link to="/" className="hover:text-brand-navy">Home</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to="/shop" className="hover:text-brand-navy">Shop</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-800 line-clamp-1">{product.product_name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="relative aspect-square bg-white rounded-2xl overflow-hidden border border-slate-200">
            {images[selectedImage] ? (
              <img src={images[selectedImage]} alt={product.product_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-slate-100 text-slate-400" data-testid="no-image-detail">
                <ImageIcon className="h-12 w-12" />
                <span className="text-sm font-bold uppercase tracking-wider">Image coming soon</span>
              </div>
            )}
            {pct !== null && (
              <div className="absolute top-4 left-4 bg-brand-red text-white rounded-full h-16 w-16 flex flex-col items-center justify-center font-black shadow-lg">
                <span className="text-lg leading-none">-{pct}%</span>
                <span className="text-[9px] uppercase tracking-widest">Save</span>
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {images.map((img, i) => (
                <button key={i} data-testid={`product-image-${i}`} onClick={() => setSelectedImage(i)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 ${selectedImage === i ? "border-brand-navy" : "border-transparent"}`}>
                  <img src={img || IMG_FALLBACK} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          {product.is_demo && (
            <div className="inline-flex items-center gap-1 bg-brand-yellowLight border border-brand-yellow text-brand-navy text-[11px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
              Demo / Sample Data
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <ConditionBadge condition={product.condition} size="lg" />
            {product.badges?.map((b) => <MarketingBadge key={b} badge={b} />)}
          </div>
          {product.brand && <div className="text-sm uppercase tracking-widest text-slate-500 font-bold">{product.brand}</div>}
          <h1 className="font-heading text-3xl sm:text-4xl text-brand-navy leading-tight" data-testid="product-title">{product.product_name}</h1>

          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-3xl font-heading ${pv.onSale ? "text-brand-red" : "text-brand-navy"}`} data-testid="product-price">{money(pv.now)}</span>
            {pv.onSale && <span data-testid="product-sale-tag" className="text-[10px] font-black uppercase tracking-widest bg-brand-red text-white rounded-full px-2 py-1">Sale</span>}
          </div>
          {pv.onSale ? (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="product-price-breakdown">
              <span className="text-slate-500">Regular <span className="text-slate-400 line-through">{money(pv.regular)}</span></span>
              {pv.retail && <span className="text-slate-500" data-testid="product-retail">Retail <span className="text-slate-400 line-through">{money(pv.retail)}</span></span>}
              <span className="font-black text-brand-red">SAVE {money(pv.save)} (-{pv.pct}%)</span>
            </div>
          ) : (pv.was && pv.was > pv.now && (
            <div className="mt-1.5 text-sm" data-testid="product-price-breakdown">
              <span className="text-lg text-slate-400 line-through">{money(pv.was)}</span>
              <span className="ml-3 font-black text-brand-red">SAVE {money(pv.save)}</span>
            </div>
          ))}

          <div className="flex items-center gap-3">
            <span data-testid="pd-availability" className={`inline-flex items-center gap-1.5 text-sm font-bold ${outOfStock ? "text-slate-400" : (avail.available <= 5 ? "text-brand-red" : "text-emerald-600")}`}>
              <span className={`h-2 w-2 rounded-full ${outOfStock ? "bg-slate-400" : "bg-current"}`} />
              {outOfStock ? unavailableLabel : unique ? "One-of-a-kind · 1 available" : (avail.available <= 5 ? `Only ${avail.available} left` : `${avail.available} in stock`)}
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-xs text-slate-500">SKU: {product.sku}</span>
          </div>

          {product.short_description && <p className="text-slate-700">{product.short_description}</p>}

          {product.condition_description && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
              <div className="font-heading text-sm text-brand-navy mb-1">{product.condition}</div>
              <p className="text-sm text-slate-700">{product.condition_description}</p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <div className="inline-flex items-center border border-slate-200 rounded-full h-11 bg-white">
              <button data-testid="qty-decrement" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-full font-bold">−</button>
              <input data-testid="qty-input" value={qty} onChange={(e) => setQty(Math.max(1, Math.min(maxQty, parseInt(e.target.value || "1"))))}
                className="w-10 text-center bg-transparent font-bold" />
              <button data-testid="qty-increment" disabled={qty >= maxQty} onClick={() => setQty(Math.min(maxQty, qty + 1))} className="w-10 h-full font-bold disabled:opacity-30">+</button>
            </div>
            <button data-testid="add-to-cart-btn" onClick={onAddToCart} disabled={outOfStock}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-red disabled:opacity-40 text-white font-black uppercase tracking-wider h-11 rounded-full active:scale-95 transition">
              <ShoppingCart className="h-4 w-4" /> {outOfStock ? unavailableLabel : "Add to Cart"}
            </button>
          </div>

          <button data-testid="buy-now-btn" onClick={onBuyNow} disabled={outOfStock}
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-redHover disabled:opacity-40 text-white font-black uppercase tracking-wider h-11 rounded-full active:scale-95 transition">
            <Zap className="h-4 w-4" /> Buy Now
          </button>

          {holdCfg.hold_enabled && (product.price || 0) >= (holdCfg.hold_min_price || 0) && !outOfStock && (
            <button data-testid="hold-24-btn" onClick={onHold}
              className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-brand-yellow bg-brand-yellowLight text-brand-navy font-black uppercase tracking-wider h-11 rounded-full hover:bg-brand-yellow transition">
              <Timer className="h-4 w-4" /> Hold for 24 Hours <span className="text-[10px] font-bold ml-2 opacity-70">{money(holdCfg.hold_deposit_amount * qty)} deposit</span>
            </button>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button data-testid="pdp-wishlist-btn" onClick={() => { toggleLocalWishlist(product.id); toast(wished ? "Removed from wishlist" : "Saved to wishlist"); }}
              className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 h-10 text-sm font-bold hover:bg-slate-50">
              <Heart className={`h-4 w-4 ${wished ? "fill-brand-red text-brand-red" : ""}`} /> {wished ? "Wishlisted" : "Wishlist"}
            </button>
            <div className="inline-flex items-center gap-1 border border-slate-200 rounded-full px-2 h-10 text-sm font-bold">
              <button data-testid="share-native-btn" onClick={nativeShare} aria-label="Share"
                className="inline-flex items-center gap-1.5 px-2 h-8 rounded-full text-brand-navy hover:bg-slate-50">
                <Share2 className="h-4 w-4" /> Share
              </button>
              <span className="w-px h-5 bg-slate-200" />
              <button data-testid="share-whatsapp" onClick={() => shareTo("whatsapp")} aria-label="Share on WhatsApp" className="p-1.5 text-slate-600 hover:text-emerald-600"><MessageCircle className="h-4 w-4" /></button>
              <button data-testid="share-facebook" onClick={() => shareTo("facebook")} aria-label="Share on Facebook" className="p-1.5 text-slate-600 hover:text-blue-600"><Facebook className="h-4 w-4" /></button>
              <button data-testid="share-twitter" onClick={() => shareTo("twitter")} aria-label="Share on X" className="p-1.5 text-slate-600 hover:text-brand-navy"><Twitter className="h-4 w-4" /></button>
              <button data-testid="share-copy" onClick={copyLink} aria-label="Copy link" className="p-1.5 text-slate-600 hover:text-brand-red"><Copy className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-4">
            <InfoCard icon={Truck} title="Drive-Through Pickup" text="Pay online now, then drive to our Barrhaven pickup lane to collect your order." accent />
            <InfoCard icon={ShieldCheck} title="Inspected & Tested" text="Every item is inspected and tested, then prepared for your drive-through pickup." />
          </div>
        </div>
      </div>

      {product.description && (
        <section className="mt-12 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-heading text-xl text-brand-navy mb-3">Description</h2>
            <p className="text-slate-700 whitespace-pre-line leading-relaxed">{product.description}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-heading text-xl text-brand-navy mb-3">Details</h2>
            <dl className="text-sm space-y-2">
              <Detail label="SKU" value={product.sku} />
              <Detail label="Condition" value={product.condition} />
              <Detail label="Brand" value={product.brand || "—"} />
              <Detail label="Availability" value={outOfStock ? unavailableLabel : unique ? "1 available" : `${avail.available} available`} />
              {product.dimensions && <Detail label="Dimensions" value={product.dimensions} />}
              {product.weight && <Detail label="Weight" value={`${product.weight} kg`} />}
            </dl>
          </div>
        </section>
      )}
      {hold && <HoldModal product={product} qty={qty} deposit={holdCfg.hold_deposit_amount} onClose={() => setHold(null)} />}
    </main>
  );
}

const Detail = ({ label, value }) => (
  <div className="flex justify-between border-b border-slate-100 py-1.5">
    <dt className="text-slate-500">{label}</dt>
    <dd className="font-bold text-slate-800">{value}</dd>
  </div>
);

const InfoCard = ({ icon: Icon, title, text, accent }) => (
  <div className={`rounded-xl p-3 border ${accent ? "bg-brand-greenLight border-brand-green/30 text-brand-greenDark" : "bg-slate-50 border-slate-200 text-slate-700"}`}>
    <Icon className={`h-5 w-5 ${accent ? "text-brand-green" : "text-brand-navy"}`} />
    <div className="font-bold text-sm mt-1">{title}</div>
    <div className="text-xs mt-0.5">{text}</div>
  </div>
);
